import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  User,
  Calendar,
  Loader2,
  AlertCircle,
  PhoneCall,
  FileText,
} from "lucide-react";
import { leadsApi } from "../services/api";

function LeadHistoryModal({ isOpen, onClose, lead }) {
  const [activeTab, setActiveTab] = useState("call_logs"); // 'call_logs' | 'assign_history' | 'lead_status'

  // Dynamic state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [leadDetails, setLeadDetails] = useState(lead || null);

  // Tab Data
  const [callsData, setCallsData] = useState({
    items: [],
    totalCount: 0,
    page: 0,
    totalPages: 1,
    loadingMore: false,
  });

  const [assignData, setAssignData] = useState({
    items: [],
    totalCount: 0,
    page: 0,
    totalPages: 1,
    loadingMore: false,
  });

  const [statusData, setStatusData] = useState({
    items: [],
    totalCount: 0,
    page: 0,
    totalPages: 1,
    loadingMore: false,
  });

  // Numeric Lead ID extraction
  const getNumericLeadId = useCallback(() => {
    if (!lead) return null;
    if (typeof lead === "number") return lead;
    if (typeof lead === "string") {
      const trimmed = lead.trim();
      if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);
      if (trimmed.toUpperCase().startsWith("LD")) {
        const numOnly = trimmed.replace(/^LD0*/i, "");
        if (/^\d+$/.test(numOnly)) return parseInt(numOnly, 10);
      }
    }
    const candidate = lead.id ?? lead.leadId ?? lead.lead_id;
    if (candidate !== undefined && candidate !== null) {
      if (typeof candidate === "number") return candidate;
      const strVal = String(candidate).trim();
      if (/^\d+$/.test(strVal)) return parseInt(strVal, 10);
      if (strVal.toUpperCase().startsWith("LD")) {
        const numOnly = strVal.replace(/^LD0*/i, "");
        if (/^\d+$/.test(numOnly)) return parseInt(numOnly, 10);
      }
    }
    return null;
  }, [lead]);

  // Format ISO Date to 12-hour format for Call Logs (e.g. 16/03/2024, 4:09 pm)
  const formatDateTime12 = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;

      return `${day}/${month}/${year}, ${hours}:${minutes} ${ampm}`;
    } catch (e) {
      return dateStr;
    }
  };

  // Format ISO Date to 24-hour format for Assign & Status tabs (e.g. 18/03/2024, 16:09)
  const formatDateTime24 = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day}/${month}/${year}, ${hours}:${minutes}`;
    } catch (e) {
      return dateStr;
    }
  };

  // Strip 'LD' and leading zeros from display ID (e.g. LD000234564 -> 234564 or LD83459596 -> 83459596)
  const formatDisplayId = (displayId, fallbackId) => {
    const val = displayId || fallbackId;
    if (!val) return "—";
    const strVal = String(val).trim();
    const cleaned = strVal.replace(/^LD0*/i, "");
    return cleaned || strVal;
  };

  // Fetch full dynamic history from API
  const fetchLeadHistory = useCallback(async () => {
    const leadId = getNumericLeadId();
    if (!leadId) {
      setError("Valid Lead ID not found.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await leadsApi.getLeadHistory(leadId);

      if (response) {
        if (response.lead) {
          setLeadDetails(response.lead);
        }

        // 1. Calls Data
        if (response.calls && Array.isArray(response.calls.items)) {
          setCallsData({
            items: response.calls.items,
            totalCount: response.calls.totalCount ?? response.calls.items.length,
            page: response.calls.page ?? 0,
            totalPages: response.calls.totalPages ?? 1,
            loadingMore: false,
          });
        } else if (Array.isArray(response.callLogs)) {
          setCallsData({
            items: response.callLogs,
            totalCount: response.callLogs.length,
            page: 0,
            totalPages: 1,
            loadingMore: false,
          });
        } else {
          setCallsData({ items: [], totalCount: 0, page: 0, totalPages: 1, loadingMore: false });
        }

        // 2. Assignments Data
        let assignItems = [];
        let totalAssignCount = 0;
        let assignPage = 0;
        let assignTotalPages = 1;

        if (response.assignments && Array.isArray(response.assignments.items) && response.assignments.items.length > 0) {
          assignItems = response.assignments.items;
          totalAssignCount = response.assignments.totalCount ?? response.assignments.items.length;
          assignPage = response.assignments.page ?? 0;
          assignTotalPages = response.assignments.totalPages ?? 1;
        } else if (Array.isArray(response.assignHistory) && response.assignHistory.length > 0) {
          assignItems = response.assignHistory;
          totalAssignCount = response.assignHistory.length;
        } else if (response.lead && response.lead.assignedToName) {
          // If no assignment changes yet, show initial assignment from lead object
          assignItems = [
            {
              id: response.lead.id,
              leadDisplayId: response.lead.leadId,
              leadName: response.lead.customerName,
              leadPhone: response.lead.contactNumber,
              assignedToName: response.lead.assignedToName,
              assignedByName: response.lead.createdByName || "System",
              assignedAt: response.lead.createdAt || response.lead.updatedAt,
            },
          ];
          totalAssignCount = 1;
        }

        setAssignData({
          items: assignItems,
          totalCount: totalAssignCount,
          page: assignPage,
          totalPages: assignTotalPages,
          loadingMore: false,
        });

        // 3. Statuses Data
        let statusItems = [];
        let totalStatusCount = 0;
        let statusPage = 0;
        let statusTotalPages = 1;

        if (response.statuses && Array.isArray(response.statuses.items) && response.statuses.items.length > 0) {
          statusItems = response.statuses.items;
          totalStatusCount = response.statuses.totalCount ?? response.statuses.items.length;
          statusPage = response.statuses.page ?? 0;
          statusTotalPages = response.statuses.totalPages ?? 1;
        } else if (Array.isArray(response.statusHistory) && response.statusHistory.length > 0) {
          statusItems = response.statusHistory;
          totalStatusCount = response.statusHistory.length;
        } else if (response.lead && (response.lead.status || response.lead.dispositionName)) {
          // If no status history yet, show initial status from lead object
          statusItems = [
            {
              id: response.lead.id,
              leadDisplayId: response.lead.leadId,
              status: response.lead.status,
              statusLabel: response.lead.status,
              dispositionName: response.lead.dispositionName || response.lead.status,
              changedByName: response.lead.assignedToName || response.lead.createdByName || "System",
              changedAt: response.lead.updatedAt || response.lead.createdAt,
            },
          ];
          totalStatusCount = 1;
        }

        setStatusData({
          items: statusItems,
          totalCount: totalStatusCount,
          page: statusPage,
          totalPages: statusTotalPages,
          loadingMore: false,
        });
      }
    } catch (err) {
      console.error("Error fetching lead history from API:", err);
      setError(err?.message || "Failed to load lead history from server.");
    } finally {
      setLoading(false);
    }
  }, [getNumericLeadId]);

  useEffect(() => {
    if (isOpen) {
      setLeadDetails(lead);
      fetchLeadHistory();
    } else {
      setCallsData({ items: [], totalCount: 0, page: 0, totalPages: 1, loadingMore: false });
      setAssignData({ items: [], totalCount: 0, page: 0, totalPages: 1, loadingMore: false });
      setStatusData({ items: [], totalCount: 0, page: 0, totalPages: 1, loadingMore: false });
      setError(null);
    }
  }, [isOpen, lead, fetchLeadHistory]);

  // Load More Pagination per tab
  const handleLoadMore = async (tab) => {
    const leadId = getNumericLeadId();
    if (!leadId) return;

    try {
      if (tab === "call_logs") {
        const nextPage = callsData.page + 1;
        setCallsData((prev) => ({ ...prev, loadingMore: true }));
        const res = await leadsApi.getLeadCallHistory(leadId, nextPage, 20);
        if (res && Array.isArray(res.items)) {
          setCallsData((prev) => ({
            ...prev,
            items: [...prev.items, ...res.items],
            page: res.page ?? nextPage,
            totalPages: res.totalPages ?? prev.totalPages,
            totalCount: res.totalCount ?? prev.totalCount,
            loadingMore: false,
          }));
        }
      } else if (tab === "assign_history") {
        const nextPage = assignData.page + 1;
        setAssignData((prev) => ({ ...prev, loadingMore: true }));
        const res = await leadsApi.getLeadAssignHistory(leadId, nextPage, 20);
        if (res && Array.isArray(res.items)) {
          setAssignData((prev) => ({
            ...prev,
            items: [...prev.items, ...res.items],
            page: res.page ?? nextPage,
            totalPages: res.totalPages ?? prev.totalPages,
            totalCount: res.totalCount ?? prev.totalCount,
            loadingMore: false,
          }));
        }
      } else if (tab === "lead_status") {
        const nextPage = statusData.page + 1;
        setStatusData((prev) => ({ ...prev, loadingMore: true }));
        const res = await leadsApi.getLeadStatusHistory(leadId, nextPage, 20);
        if (res && Array.isArray(res.items)) {
          setStatusData((prev) => ({
            ...prev,
            items: [...prev.items, ...res.items],
            page: res.page ?? nextPage,
            totalPages: res.totalPages ?? prev.totalPages,
            totalCount: res.totalCount ?? prev.totalCount,
            loadingMore: false,
          }));
        }
      }
    } catch (err) {
      console.error(`Error loading more for ${tab}:`, err);
    } finally {
      if (tab === "call_logs") setCallsData((prev) => ({ ...prev, loadingMore: false }));
      if (tab === "assign_history") setAssignData((prev) => ({ ...prev, loadingMore: false }));
      if (tab === "lead_status") setStatusData((prev) => ({ ...prev, loadingMore: false }));
    }
  };

  // Status Badge Colors based on status key
  const getStatusBadgeClass = (statusKey, statusLabel) => {
    const s = ((statusKey || statusLabel || "") + "").toUpperCase();
    if (s === "ORDER_PLACED" || s.includes("ORDER") || s.includes("PLACED")) {
      return "border border-emerald-400 text-emerald-600 bg-emerald-50/60";
    }
    if (
      s === "CALL_BACK" ||
      s === "FUTURE_CALL_BACK" ||
      s === "INTERESTED_ON_HOLD" ||
      s.includes("CALLBACK") ||
      s.includes("CALL BACK") ||
      s.includes("HOLD") ||
      s.includes("INTERESTED")
    ) {
      return "border border-amber-400 text-amber-500 bg-amber-50/40";
    }
    if (s === "NOT_INTERESTED" || s === "PRANK_CALL" || s.includes("NOT_INTERESTED") || s.includes("PRANK")) {
      return "border border-rose-400 text-rose-500 bg-rose-50/40";
    }
    return "border border-gray-400 text-gray-500 bg-gray-50/60";
  };

  // Call Icon & Badge Circle based on callType
  const getCallIcon = (callType) => {
    const type = (callType || "").toUpperCase();
    if (type === "INCOMING") {
      return {
        wrapper: "border-emerald-400 text-emerald-500 bg-emerald-50/40",
        icon: <PhoneIncoming className="w-4 h-4 text-emerald-500" />,
        typeLabel: "Incoming call",
      };
    }
    if (type === "OUTGOING") {
      return {
        wrapper: "border-blue-400 text-blue-500 bg-blue-50/40",
        icon: <PhoneOutgoing className="w-4 h-4 text-blue-500" />,
        typeLabel: "Outgoing call",
      };
    }
    return {
      wrapper: "border-rose-400 text-rose-500 bg-rose-50/40",
      icon: <PhoneMissed className="w-4 h-4 text-rose-500" />,
      typeLabel: "Missed call",
    };
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      {/* Modal Container */}
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-gray-100 transform transition-all relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-[#bdf3db] px-6 py-3.5 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-800 tracking-wide">
            Lead History
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-700 hover:bg-black/10 transition-colors focus:outline-none"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-6 pt-2 flex items-center space-x-7 bg-white">
          <button
            onClick={() => setActiveTab("call_logs")}
            className={`pb-2.5 text-xs font-semibold transition-all relative inline-flex items-center ${
              activeTab === "call_logs"
                ? "text-teal-700 border-b-2 border-teal-500 font-bold"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <span>Call logs</span>
            {(callsData.totalCount > 0 || callsData.items.length > 0) && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-gray-600 leading-none">
                {callsData.totalCount || callsData.items.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("assign_history")}
            className={`pb-2.5 text-xs font-semibold transition-all relative inline-flex items-center ${
              activeTab === "assign_history"
                ? "text-teal-700 border-b-2 border-teal-500 font-bold"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <span>Assign History</span>
            {(assignData.totalCount > 0 || assignData.items.length > 0) && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-gray-600 leading-none">
                {assignData.totalCount || assignData.items.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("lead_status")}
            className={`pb-2.5 text-xs font-semibold transition-all relative inline-flex items-center ${
              activeTab === "lead_status"
                ? "text-teal-700 border-b-2 border-teal-500 font-bold"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <span>Lead status</span>
            {(statusData.totalCount > 0 || statusData.items.length > 0) && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-gray-600 leading-none">
                {statusData.totalCount || statusData.items.length}
              </span>
            )}
          </button>
        </div>

        {/* Tab Contents */}
        <div className="max-h-[380px] min-h-[220px] overflow-y-auto px-6 py-1 divide-y divide-gray-100">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600 mb-2" />
              <p className="text-xs font-medium text-gray-500">Loading lead history...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-9 h-9 text-rose-500 mb-2" />
              <p className="text-xs font-medium text-gray-700 max-w-sm">{error}</p>
              <button
                onClick={fetchLeadHistory}
                className="mt-3 px-4 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* TAB 1: CALL LOGS */}
              {activeTab === "call_logs" && (
                <div>
                  {callsData.items.length === 0 ? (
                    <div className="py-14 text-center text-gray-400">
                      <PhoneCall className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-xs font-medium">No call logs found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {callsData.items.map((log, index) => {
                        const callConfig = getCallIcon(log.callType || log.callStatus || log.type);
                        const durationStr =
                          log.durationText ??
                          (log.durationSeconds !== null && log.durationSeconds !== undefined
                            ? `${log.durationSeconds} sec`
                            : log.callDurationSeconds !== null && log.callDurationSeconds !== undefined
                            ? `${log.callDurationSeconds} sec`
                            : "—");
                        const phone =
                          log.maskedPhone ||
                          log.phone ||
                          log.contactNumber ||
                          leadDetails?.contactNumber ||
                          leadDetails?.phone ||
                          "—";
                        const agent = log.agentName || log.by || log.employeeName || "System";
                        const dateVal = log.calledAt || log.callTime || log.date || log.createdAt;

                        return (
                          <div
                            key={log.id || index}
                            className="py-3.5 flex items-center justify-between hover:bg-gray-50/50 px-1 rounded-lg transition-colors"
                          >
                            <div className="flex items-center space-x-3.5">
                              {/* Icon Circle */}
                              <div
                                className={`w-9 h-9 rounded-full border flex items-center justify-center flex-shrink-0 ${callConfig.wrapper}`}
                              >
                                {callConfig.icon}
                              </div>

                              {/* Number & Caller */}
                              <div>
                                <p className="text-xs font-bold text-gray-800 tracking-wider">
                                  {phone}
                                </p>
                                <p className="text-[11px] text-gray-500 mt-0.5">
                                  By, {agent}
                                </p>
                              </div>
                            </div>

                            {/* Date & Duration */}
                            <div className="text-right">
                              <p className="text-[11px] font-medium text-gray-800">
                                {formatDateTime12(dateVal)}
                              </p>
                              <p className="text-[11px] text-gray-500 mt-0.5">
                                {callConfig.typeLabel}, {durationStr}
                              </p>
                            </div>
                          </div>
                        );
                      })}

                      {/* Pagination Load More */}
                      {callsData.page + 1 < callsData.totalPages && (
                        <div className="py-2.5 text-center">
                          <button
                            onClick={() => handleLoadMore("call_logs")}
                            disabled={callsData.loadingMore}
                            className="text-xs font-semibold text-teal-700 hover:text-teal-800 bg-teal-50 px-4 py-1.5 rounded-lg border border-teal-200 transition-colors disabled:opacity-50 inline-flex items-center space-x-1.5"
                          >
                            {callsData.loadingMore && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            <span>Load More Calls</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* TAB 2: ASSIGN HISTORY */}
              {activeTab === "assign_history" && (
                <div>
                  {assignData.items.length === 0 ? (
                    <div className="py-14 text-center text-gray-400">
                      <User className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-xs font-medium">No assignment history found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {assignData.items.map((item, index) => {
                        const displayId = formatDisplayId(item.leadDisplayId || item.leadId, leadDetails?.id || leadDetails?.leadId);
                        const customerName =
                          item.leadName || item.customerName || leadDetails?.customerName || leadDetails?.name || "—";
                        const phone =
                          item.leadPhone || item.phone || item.contactNumber || leadDetails?.contactNumber || leadDetails?.phone || "—";
                        const dateFormatted = formatDateTime24(item.assignedAt || item.date || item.createdAt);
                        const assignedTo = item.assignedToName || item.assignedTo || item.assignTo || "Unassigned";
                        const assignedBy = item.assignedByName || item.assignedBy || item.createdByName || "System";

                        return (
                          <div
                            key={item.id || index}
                            className="py-3.5 flex items-start justify-between hover:bg-gray-50/50 px-1 rounded-lg transition-colors"
                          >
                            {/* Left details */}
                            <div className="space-y-1">
                              <p className="text-xs font-bold text-gray-800">
                                {displayId}
                              </p>
                              <div className="flex items-center space-x-2 text-[11px] text-gray-700">
                                <User className="w-3.5 h-3.5 text-emerald-600 inline flex-shrink-0" />
                                <span>{customerName}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-[11px] text-gray-700">
                                <Phone className="w-3.5 h-3.5 text-emerald-600 inline flex-shrink-0" />
                                <span>{phone}</span>
                              </div>
                            </div>

                            {/* Right details */}
                            <div className="text-right space-y-1">
                              <div className="flex items-center justify-end space-x-1.5 text-[11px] font-medium text-gray-800">
                                <Calendar className="w-3.5 h-3.5 text-gray-700 inline" />
                                <span>{dateFormatted}</span>
                              </div>
                              <p className="text-[11px] text-gray-500">
                                Assign to:{" "}
                                <span className="font-medium text-gray-800">
                                  {assignedTo}
                                </span>
                              </p>
                              <p className="text-[11px] text-gray-500">
                                Assigned by:{" "}
                                <span className="font-medium text-gray-800">
                                  {assignedBy}
                                </span>
                              </p>
                            </div>
                          </div>
                        );
                      })}

                      {/* Pagination Load More */}
                      {assignData.page + 1 < assignData.totalPages && (
                        <div className="py-2.5 text-center">
                          <button
                            onClick={() => handleLoadMore("assign_history")}
                            disabled={assignData.loadingMore}
                            className="text-xs font-semibold text-teal-700 hover:text-teal-800 bg-teal-50 px-4 py-1.5 rounded-lg border border-teal-200 transition-colors disabled:opacity-50 inline-flex items-center space-x-1.5"
                          >
                            {assignData.loadingMore && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            <span>Load More Assignments</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* TAB 3: LEAD STATUS */}
              {activeTab === "lead_status" && (
                <div>
                  {statusData.items.length === 0 ? (
                    <div className="py-14 text-center text-gray-400">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-xs font-medium">No status history found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {statusData.items.map((item, index) => {
                        const displayId = formatDisplayId(item.leadDisplayId || item.leadId, leadDetails?.id || leadDetails?.leadId);
                        const changedBy =
                          item.changedByName || item.changedBy || item.by || item.employeeName || "System";
                        const displayStatus =
                          item.dispositionName || item.statusLabel || item.status || "—";
                        const dateFormatted = formatDateTime24(item.changedAt || item.date || item.createdAt || item.updatedAt);

                        return (
                          <div
                            key={item.id || index}
                            className="py-3.5 flex items-center justify-between hover:bg-gray-50/50 px-1 rounded-lg transition-colors"
                          >
                            {/* Left details */}
                            <div>
                              <p className="text-xs font-bold text-gray-800">
                                {displayId}
                              </p>
                              <p className="text-[11px] text-gray-500 mt-0.5">
                                By, {changedBy}
                              </p>
                            </div>

                            {/* Right status badge & date */}
                            <div className="text-right flex flex-col items-end space-y-1.5">
                              <span
                                className={`px-3.5 py-0.5 text-[11px] font-normal rounded-lg inline-block ${getStatusBadgeClass(
                                  item.dispositionName || item.status,
                                  item.statusLabel || item.status
                                )}`}
                              >
                                {displayStatus}
                              </span>
                              <div className="flex items-center space-x-1.5 text-[11px] font-medium text-gray-800">
                                <Calendar className="w-3.5 h-3.5 text-gray-700 inline" />
                                <span>{dateFormatted}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Pagination Load More */}
                      {statusData.page + 1 < statusData.totalPages && (
                        <div className="py-2.5 text-center">
                          <button
                            onClick={() => handleLoadMore("lead_status")}
                            disabled={statusData.loadingMore}
                            className="text-xs font-semibold text-teal-700 hover:text-teal-800 bg-teal-50 px-4 py-1.5 rounded-lg border border-teal-200 transition-colors disabled:opacity-50 inline-flex items-center space-x-1.5"
                          >
                            {statusData.loadingMore && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            <span>Load More Status Logs</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeadHistoryModal;
